import { INeotasteMapProps } from '@/components/map/utils/neotaste-map-types'
import { Locale } from '@/setup/locales'
import { run } from '@/utils/run'

interface Coordinates {
    latitude: number
    longitude: number
}

interface Bounds {
    southWest: Coordinates
    northEast: Coordinates
}

export const boundsToGoogleLatLngBounds = ({ southWest, northEast }: Bounds) => {
    return {
        /**
         * East longitude in degrees. Values outside the range [-180, 180] will be
         * wrapped to the range [-180, 180). For example, a value of -190 will be
         * converted to 170. A value of 190 will be converted to -170. This reflects
         * the fact that longitudes wrap around the globe.
         */
        east: northEast.longitude,
        /**
         * North latitude in degrees. Values will be clamped to the range [-90, 90].
         * This means that if the value specified is less than -90, it will be set
         * to -90. And if the value is greater than 90, it will be set to 90.
         */
        north: northEast.latitude,
        /**
         * South latitude in degrees. Values will be clamped to the range [-90, 90].
         * This means that if the value specified is less than -90, it will be set
         * to -90. And if the value is greater than 90, it will be set to 90.
         */
        south: southWest.latitude,
        /**
         * West longitude in degrees. Values outside the range [-180, 180] will be
         * wrapped to the range [-180, 180). For example, a value of -190 will be
         * converted to 170. A value of 190 will be converted to -170. This reflects
         * the fact that longitudes wrap around the globe.
         */
        west: southWest.longitude,
    }
}

export const coordinateToGoogleLatLng = ({ latitude, longitude }: Coordinates) => {
    return { lat: latitude, lng: longitude }
}

export function getNeotasteMapPositionProps(
    locale: Locale,
    override?: Partial<Record<Locale, Partial<INeotasteMapProps>>>
): Pick<INeotasteMapProps, 'centerDesktop' | 'centerMobile' | 'zoomDesktop' | 'zoomMobile'> {
    const defaultPosition = run(() => {
        switch (locale) {
            case Locale.DE_DE:
            case Locale.EN_DE:
            case Locale.DE_AT:
            case Locale.EN_AT:
                return {
                    centerDesktop: { lat: 51.3, lng: 9.9 },
                    centerMobile: { lat: 51, lng: 9.5 },
                    zoomDesktop: 6.8,
                    zoomMobile: 5.3,
                }

            case Locale.EN_GB:
                return {
                    centerDesktop: { lat: 53.2, lng: 3.1 },
                    centerMobile: { lat: 51.2, lng: 3.1 },
                    zoomDesktop: 6,
                    zoomMobile: 5,
                }

            case Locale.EN_NL:
            case Locale.NL_NL:
                return {
                    centerDesktop: { lat: 52.2, lng: 5.5 },
                    centerMobile: { lat: 52.2, lng: 5.5 },
                    zoomDesktop: 6.8,
                    zoomMobile: 6.3,
                }
        }
    })

    return {
        ...defaultPosition,
        ...override?.[locale],
    }
}
